import { memo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components/macro";

import { LoadingOverlay } from "../../../../components/Loading";
import Page, { PageButtonLink } from "../../../../components/Page";

import { useAccountId } from "../../../../auth/useAccountId";
import Icon from "../../../../components/Icons";
import { H1 } from "../../../../components/Typography";
import { BillingPayoutRule } from "../../../../types/billing";
import type { PayoutAccountService } from "../../../../types/management-auth/generated";
import useAccount from "../../../account/hooks/useAccount";
import SplitPaymentBox from "../../../additionalServices/components/SplitPaymentBox";
import { Wrapper } from "../../../additionalServices/components/elements";
import { useCheckoutConfig } from "../../../useCheckoutConfig";
import { usePayoutRules } from "../../hooks/usePayoutRules";
import Filters from "./Filters";
import PayoutRulesTable from "./PayoutRulesTable";

type PayoutRulesAccountSectionProps = {
    payoutRules: BillingPayoutRule[];
    accountId: string;
};

const sortRules = (rules: BillingPayoutRule[]) => {
    const active = rules.filter((x) => !x.deleted_at);
    const deleted = rules.filter((x) => x.deleted_at);
    return [...active, ...deleted];
};

const PayoutRulesAccountSection = memo(
    ({ payoutRules, accountId }: PayoutRulesAccountSectionProps) => {
        return (
            <MarginTopWrapper>
                <PayoutRulesTable
                    payoutRules={sortRules(payoutRules)}
                    accountId={accountId}
                />
            </MarginTopWrapper>
        );
    },
);

export const PayoutRulesList = () => {
    const { t } = useTranslation();
    const accountId = useAccountId();
    const { account } = useAccount();
    const history = useHistory();
    const location = useLocation();
    const params = new URLSearchParams(location.search.slice(1));

    const { checkoutConfig } = useCheckoutConfig(accountId);
    const { payoutRules, isLoading } = usePayoutRules(params);

    const hasRules = Boolean(payoutRules && (payoutRules || []).length > 0);

    const features = account?.services?.payout
        ?.features as PayoutAccountService["features"];

    const splitPaymentEnabled = features?.split_payment?.status === "enabled";

    return (
        <Page
            title={
                <>
                    <Title>{t("payout_rules.payout_rules.title")}</Title>
                    {splitPaymentEnabled && (
                        <PageButtonLink
                            to={`/${accountId}/settings/payout-rules/new`}
                        >
                            <Icon icon="plus" fill="currentColor" />
                            {t("payout_rules.payout_rules.create_new")}
                        </PageButtonLink>
                    )}
                </>
            }
            hasEnvFrame={false}
        >
            {splitPaymentEnabled && (
                <Filters
                    history={history}
                    location={location}
                    checkoutConfig={checkoutConfig}
                />
            )}
            {splitPaymentEnabled && (
                <PayoutRulesAccountSection
                    accountId={accountId}
                    payoutRules={payoutRules || []}
                />
            )}
            {!splitPaymentEnabled && !isLoading && (
                <MarginTopWrapper>
                    <RelativeGroup>
                        {((account?.services?.payout?.features?.status !==
                            "enabled" &&
                            account?.services?.payout?.features?.features
                                ?.split_payment?.status !== "enabled") ||
                            !hasRules) && (
                            <Wrapper>
                                <SplitPaymentBox />
                            </Wrapper>
                        )}
                    </RelativeGroup>
                </MarginTopWrapper>
            )}
            {isLoading && <LoadingOverlay />}
        </Page>
    );
};

const RelativeGroup = styled.div`
    position: relative;
    box-sizing: border-box;
    overflow: hidden;
`;

const Title = styled(H1)`
    display: inline-block;
        /* Heading/H6/Medium/Desktop */
        font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: 28px; /* 140% */
    letter-spacing: -0.4px;
`;
const MarginTopWrapper = styled.div`
`;
